import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';

import { feedApi, feedKeys, findCachedPost, type StreamParams } from './api';
import { seedCommentThreads } from './lib/cache';

export function useFeedStream(params: StreamParams) {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: feedKeys.stream(params),
    queryFn: async ({ pageParam, signal }) => {
      const page = await feedApi.stream(params, pageParam, signal);
      seedCommentThreads(queryClient, page.items);
      return page;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function usePinnedPosts(groupId?: string | null, enabled = true) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: feedKeys.pinned(groupId),
    queryFn: async ({ signal }) => {
      const posts = await feedApi.pinned(groupId, signal);
      seedCommentThreads(queryClient, posts);
      return posts;
    },
    enabled,
  });
}

/** A single post; starts from a copy already in the stream or pinned caches. */
export function usePost(id: string) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: feedKeys.post(id),
    queryFn: async ({ signal }) => {
      const post = await feedApi.post(id, signal);
      seedCommentThreads(queryClient, [post]);
      return post;
    },
    initialData: () => findCachedPost(queryClient, id),
    initialDataUpdatedAt: 0,
  });
}

export function useFeedCounters(enabled = true) {
  return useQuery({
    queryKey: feedKeys.counters,
    queryFn: ({ signal }) => feedApi.counters(signal),
    staleTime: 60_000,
    enabled,
  });
}

export function useUnreadImportant() {
  return useQuery({
    queryKey: feedKeys.importantUnread,
    queryFn: ({ signal }) => feedApi.importantUnread(signal),
  });
}

export function useGroupFeedAccess(groupId: string) {
  return useQuery({
    queryKey: feedKeys.groupAccess(groupId),
    queryFn: ({ signal }) => feedApi.groupAccess(groupId, signal),
  });
}
